import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./novakey.css";
import products from "./data/novakeyProducts";
import Checkout from "./components/Checkout";
import {
  policies as somersetPolicies,
  policyNav as somersetPolicyNav,
  policyWordCount,
} from "./data/somersetPolicies";
const cats = ["ALL", "KEYBOARDS", "MICE", "AUDIO", "DESK"];

const readRoute = (pathname, search) => {
  const parts = pathname.split("/").filter(Boolean);
  const category = new URLSearchParams(search).get("category") || "ALL";

  if (parts[0] === "product" && parts[1]) {
    const product = products.find((item) => String(item.id) === parts[1]);
    return product ? { page: "detail", selected: product } : { page: "shop" };
  }
  if (parts[0] === "shop" || pathname === "/product") {
    return { page: "shop", filter: cats.includes(category) ? category : "ALL" };
  }
  if (parts[0] === "policies" && somersetPolicies[parts[1]]) {
    return { page: "policy", policy: parts[1] };
  }
  if (somersetPolicies[parts[0]]) {
    return { page: "policy", policy: parts[0] };
  }
  if (["cart", "checkout"].includes(parts[0])) return { page: parts[0] };
  return { page: "home" };
};
/* eslint-disable no-unused-vars -- retained legacy copy is not rendered */
const policies = {
  about: {
    title: "About NOVAKEY",
    intro:
      "NOVAKEY is an independent design studio focused on the tools between thought and action. We make calm, durable objects for people who spend their days creating.",
    sections: [
      [
        "One connected system",
        "A keyboard, mouse or speaker should not compete for attention. We develop proportion, finish, sound and interaction together so every object feels related and the desk remains visually quiet.",
      ],
      [
        "Designed from use",
        "Our process begins with observation: how hands move, where cables collect and which small interruptions break concentration. Prototypes are tested through long working days before a design is approved.",
      ],
      [
        "Made to stay",
        "We favor standard fasteners, replaceable wear parts and finishes that age honestly. Products are supported with clear care guidance, repair options and a two-year limited warranty.",
      ],
    ],
  },
  shipping: {
    title: "Shipping",
    intro:
      "We ship tracked orders from regional fulfillment partners. Available services, delivery estimates and any local charges are shown before payment.",
    sections: [
      [
        "Processing times",
        "In-stock orders normally leave the warehouse within 1–2 business days. Orders placed during launches, public holidays or inventory counts may require an additional two business days.",
      ],
      [
        "Delivery estimates",
        "Standard delivery usually takes 5–10 business days after dispatch. Express services, where offered, normally take 2–5 business days. Remote destinations can require extra transit time.",
      ],
      [
        "Tracking and address changes",
        "A tracking link is emailed when the parcel is collected. Contact support immediately if an address needs correction; once a parcel is handed to the carrier, rerouting cannot be guaranteed.",
      ],
      [
        "Duties and damaged parcels",
        "Import tax and duties are determined by the destination and are shown at checkout when collection is available. Photograph visible shipping damage before opening the parcel and contact us within 72 hours.",
      ],
    ],
  },
  returns: {
    title: "Returns",
    intro:
      "You may evaluate a NOVAKEY product for 30 days after delivery. We want returns to be clear, fair and easy to follow.",
    sections: [
      [
        "Return eligibility",
        "Products must be returned clean, undamaged and complete with their original accessories. Light evaluation is expected; heavy wear, engraving, modification or missing components may reduce the refund.",
      ],
      [
        "Starting a return",
        "Email support with the order number and reason for return. We will provide a return authorization and the correct warehouse address. Unregistered parcels may be delayed or refused.",
      ],
      [
        "Refund timing",
        "Returns are inspected within five business days of arrival. Approved refunds are issued to the original payment method; banks may require a further 3–10 business days to post the credit.",
      ],
      [
        "Faults and exchanges",
        "For a confirmed fault or incorrect item, NOVAKEY covers reasonable return shipping and sends a replacement when stock is available. Preference returns are normally sent at the customer's expense.",
      ],
    ],
  },
  warranty: {
    title: "Warranty",
    intro:
      "NOVAKEY electronic products include a two-year limited warranty against defects in materials and workmanship from the original delivery date.",
    sections: [
      [
        "What is covered",
        "Coverage includes failures of the enclosure, electronics, switches, wireless connection or charging system during normal use. A repaired or replaced product remains covered for the longer of 90 days or the original term.",
      ],
      [
        "What is not covered",
        "Normal cosmetic wear, depleted consumables, accidental impact, liquid exposure, misuse, unauthorized modification and damage caused by incompatible power equipment are excluded.",
      ],
      [
        "Making a claim",
        "Contact support with proof of purchase, the product serial number and a concise description of the issue. Photos or a short video may help us diagnose the problem without unnecessary shipping.",
      ],
      [
        "Our remedy",
        "Depending on the fault and region, we may provide troubleshooting, a replacement component, depot repair or a functionally equivalent replacement product.",
      ],
    ],
  },
  privacy: {
    title: "Privacy",
    intro:
      "We collect only the information needed to operate the store, deliver orders, provide support and improve the NOVAKEY experience.",
    sections: [
      [
        "Information we use",
        "Order and support records can include your name, contact details, delivery address, purchased products and messages to our team. Basic device and usage data helps keep the website reliable.",
      ],
      [
        "Payments and partners",
        "Full card details are processed by certified payment providers and are not stored by NOVAKEY. We share necessary information with carriers, fulfillment partners and service providers only to complete requested services.",
      ],
      [
        "Retention and security",
        "Records are retained only for operational, warranty, fraud-prevention and legal requirements. We apply access controls, encryption in transit and routine reviews to protect stored information.",
      ],
      [
        "Your choices",
        "You may request access, correction or deletion of eligible personal data and may opt out of marketing at any time. Transactional messages about an active order cannot be disabled.",
      ],
    ],
  },
  sustainability: {
    title: "Designed to last",
    intro:
      "The most sustainable object is the one you keep using. Longevity, repairability and restrained material choices guide every NOVAKEY product.",
    sections: [
      [
        "Longer useful life",
        "We use durable finishes, standard fasteners and modular assemblies where the form allows. Wear components are separated from the main enclosure so common repairs do not require replacing the whole product.",
      ],
      [
        "Materials",
        "Aluminum is used where strength and recyclability justify it. Plastics are limited to functional parts, and material families are marked where possible to make responsible sorting easier.",
      ],
      [
        "Lower-impact packaging",
        "Shipping boxes use recycled fiber, soy-based inks and paper protection in place of single-use plastic trays. Packaging is sized closely to the product to reduce empty freight volume.",
      ],
      [
        "Responsible progress",
        "We review product returns, failure patterns and transport data to guide the next design revision. Sustainability claims are updated as better evidence and supplier information become available.",
      ],
    ],
  },
  contact: {
    title: "Talk to us",
    intro:
      "Our support team can help with product selection, setup, orders, returns, warranty claims and replacement parts.",
    sections: [
      [
        "Product and order support",
        "Email support@novakey.example with your order number when relevant. For technical problems, include the product name, operating system and the troubleshooting steps already attempted.",
      ],
      [
        "Studio and press",
        "For collaboration, retail, media and design enquiries, email studio@novakey.example. Please include a brief introduction, intended market and desired timeline.",
      ],
      [
        "Response times",
        "Support is staffed Monday–Friday, 09:30–18:00 UTC+8. We normally reply within one business day; launch periods and public holidays may take slightly longer.",
      ],
    ],
  },
};
const policyNav = [
  ["about", "About"],
  ["shipping", "Shipping"],
  ["returns", "Returns"],
  ["warranty", "Warranty"],
  ["privacy", "Privacy"],
  ["sustainability", "Sustainability"],
  ["contact", "Contact"],
];
/* eslint-enable no-unused-vars */
function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialRoute = useMemo(
    () => readRoute(location.pathname, location.search),
    // The initial URL is only used to seed state; later URL changes are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [page, setPage] = useState(initialRoute.page),
    [filter, setFilter] = useState(initialRoute.filter || "ALL"),
    [selected, setSelected] = useState(initialRoute.selected || null),
    [activeImage, setActiveImage] = useState(0),
    [cart, setCart] = useState(() => {
      try {
        return JSON.parse(localStorage.getItem("somerset-cart") || "[]");
      } catch {
        return [];
      }
    }),
    [menu, setMenu] = useState(false),
    [policy, setPolicy] = useState(initialRoute.policy || "shipping");
  useEffect(() => {
    const route = readRoute(location.pathname, location.search);
    setPage(route.page);
    if (route.filter) setFilter(route.filter);
    if (route.selected) setSelected(route.selected);
    if (route.policy) setPolicy(route.policy);
    setMenu(false);
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);
  useEffect(
    () => localStorage.setItem("somerset-cart", JSON.stringify(cart)),
    [cart],
  );
  const list = useMemo(
    () =>
      filter === "ALL"
        ? products
        : products.filter((p) => p.category === filter),
    [filter],
  );
  const go = (p) => {
    setPage(p);
    setMenu(false);
    const paths = {
      home: "/",
      shop: `/shop?category=${encodeURIComponent(filter)}`,
      cart: "/cart",
      checkout: "/checkout",
    };
    navigate(paths[p] || "/");
    window.scrollTo(0, 0);
  };
  const shop = (c) => {
    setFilter(c);
    setPage("shop");
    setMenu(false);
    navigate(`/shop?category=${encodeURIComponent(c)}`);
    window.scrollTo(0, 0);
  };
  const open = (p) => {
    setSelected(p);
    setActiveImage(0);
    setPage("detail");
    navigate(`/product/${p.id}`);
    window.scrollTo(0, 0);
  };
  const add = (p) =>
    setCart((c) => {
      const hit = c.find((x) => x.id === p.id);
      return hit
        ? c.map((x) => (x.id === p.id ? { ...x, qty: x.qty + 1 } : x))
        : [...c, { ...p, qty: 1 }];
    });
  const count = cart.reduce((s, x) => s + x.qty, 0),
    total = cart.reduce((s, x) => s + x.price * x.qty, 0);
  const showPolicy = (k) => {
    setPolicy(k);
    setPage("policy");
    setMenu(false);
    navigate(`/policies/${k}`);
    window.scrollTo(0, 0);
  };

  if (page === "checkout") {
    return (
      <Checkout
        cartItemsOverride={cart.map(({ qty, ...item }) => ({
          ...item,
          quantity: qty,
        }))}
        onBack={() => go("cart")}
        onShowPolicy={showPolicy}
      />
    );
  }

  return (
    <div className="nova">
      <div className="announcement">
        FREE GLOBAL DELIVERY OVER $120 · 30-DAY TRIAL
      </div>
      <header>
        <button className="brand" onClick={() => go("home")}>
          <i />
          somerset
        </button>
        <nav className={menu ? "open" : ""}>
          <button onClick={() => shop("ALL")}>SHOP ALL</button>
          <button onClick={() => shop("KEYBOARDS")}>KEYBOARDS</button>
          <button onClick={() => shop("MICE")}>MICE</button>
          <button onClick={() => shop("AUDIO")}>AUDIO</button>
          <button onClick={() => shop("DESK")}>DESK</button>
          <button onClick={() => showPolicy("terms")}>POLICIES</button>
        </nav>
        <div className="head-actions">
          <button onClick={() => go("cart")}>
            CART <b>{String(count).padStart(2, "0")}</b>
          </button>
          <button className="hamb" onClick={() => setMenu(!menu)}>
            ☰
          </button>
        </div>
      </header>
      {page === "home" && (
        <main>
          <section className="hero">
            <div className="hero-copy">
              <span className="eyebrow">NOVA SERIES / 01</span>
              <h1>
                Make space.
                <br />
                Move better.
              </h1>
              <p>
                Precision tools for clear thinking. Built around the quiet
                choreography of hands, ideas and long days at the desk.
              </p>
              <button className="primary" onClick={() => shop("ALL")}>
                EXPLORE THE SYSTEM →
              </button>
            </div>
            <figure>
              <img
                src="/images/novakey/hero.png"
                alt="Somerset original desktop collection"
              />
              <figcaption>ORIGINAL DESIGN · SERIES 01</figcaption>
            </figure>
          </section>
          <section className="section">
            <div className="section-title">
              <div>
                <span className="eyebrow">SHOP BY FLOW</span>
                <h2>
                  Your desk,
                  <br />
                  reconsidered.
                </h2>
              </div>
              <p>
                From the first keystroke to the last track of the day, each
                object is designed to feel like part of one system.
              </p>
            </div>
            <div className="category-grid">
              {cats.slice(1).map((c, i) => (
                <button key={c} onClick={() => shop(c)} className={`cat c${i}`}>
                  <span>0{i + 1}</span>
                  <b>{c}</b>
                  <small>
                    {products.filter((p) => p.category === c).length} PRODUCTS
                  </small>
                </button>
              ))}
            </div>
          </section>
          <section className="section">
            <div className="section-title">
              <h2>Current favorites.</h2>
              <button className="text-btn" onClick={() => shop("ALL")}>
                VIEW ALL 30 →
              </button>
            </div>
            <div className="product-grid">
              {products.slice(0, 8).map((p, i) => (
                <Card p={p} key={p.id} open={open} index={i} />
              ))}
            </div>
          </section>
          <section className="home-workflow section">
            <div className="section-title">
              <div>
                <span className="eyebrow">BUILT AROUND YOUR DAY</span>
                <h2>
                  One system.
                  <br />
                  Three modes of focus.
                </h2>
              </div>
              <p>
                Work changes hour by hour. Somerset tools share the same quiet
                geometry, reliable wireless language and considered tactility,
                so your setup stays coherent while the task changes.
              </p>
            </div>
            <div className="workflow-grid">
              {[
                [
                  "01",
                  "DEEP WORK",
                  "A precise keyboard, low-friction pointing and fewer interruptions for long stretches of concentrated making.",
                ],
                [
                  "02",
                  "MOVE LIGHT",
                  "Compact tools with multi-device switching make the trip from studio to meeting room feel effortless.",
                ],
                [
                  "03",
                  "RESET",
                  "Warm audio, a clear desk and hardware that disappears when the working day is finished.",
                ],
              ].map(([number, title, copy]) => (
                <article key={number}>
                  <span>{number}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
          </section>
          <section className="home-feature">
            <div className="home-feature-image">
              <img src={products[8].images[0]} alt="Somerset Studio keyboard" />
              <span>STUDIO 65 / GRAPHITE</span>
            </div>
            <div className="home-feature-copy">
              <span className="eyebrow">THE SOMERSET STANDARD</span>
              <h2>Useful from every angle.</h2>
              <p>
                We begin with the parts your hands meet every day: the edge of a
                keycap, the balance of a mouse and the resistance of a dial.
                Each decision is tested for comfort, clarity and longevity.
              </p>
              <dl>
                <div>
                  <dt>01</dt>
                  <dd>
                    <b>Quiet by design</b>
                    <span>
                      Measured acoustics and restrained visual detail.
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>02</dt>
                  <dd>
                    <b>Ready for every device</b>
                    <span>
                      Fast switching across desktop, tablet and mobile.
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>03</dt>
                  <dd>
                    <b>Made to remain useful</b>
                    <span>
                      Replaceable wear parts, clear care and two-year support.
                    </span>
                  </dd>
                </div>
              </dl>
              <button className="primary" onClick={() => open(products[8])}>
                DISCOVER STUDIO 65 →
              </button>
            </div>
          </section>
          <section className="manifesto">
            <div>
              <span className="eyebrow">DESIGNED FROM MOTION</span>
              <h2>
                Not decoration.
                <br />
                An extension of you.
              </h2>
              <p>
                We remove visual noise, tune every contact point and keep the
                parts that matter repairable. Better tools should disappear into
                the work.
              </p>
              <button className="light" onClick={() => showPolicy("terms")}>
                OUR DESIGN NOTES →
              </button>
            </div>
            <div className="orbital">
              <i />
              <i />
              <i />
            </div>
          </section>
          <section className="benefits">
            {[
              ["30 DAYS", "Risk-free trial"],
              ["2 YEARS", "Limited warranty"],
              ["REPAIRABLE", "Modular essentials"],
              ["TRACKED", "Carbon-offset delivery"],
            ].map((x) => (
              <div key={x[0]}>
                <b>{x[0]}</b>
                <span>{x[1]}</span>
              </div>
            ))}
          </section>
        </main>
      )}
      {page === "shop" && (
        <main className="shop section">
          <div className="section-title">
            <div>
              <span className="eyebrow">SOMERSET COLLECTION</span>
              <h1>Tools for focus.</h1>
            </div>
            <p>{list.length} original products · $40–$180</p>
          </div>
          <div className="filters">
            {cats.map((c) => (
              <button
                key={c}
                className={filter === c ? "active" : ""}
                onClick={() => setFilter(c)}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="product-grid">
            {list.map((p, i) => (
              <Card p={p} key={p.id} open={open} index={i} />
            ))}
          </div>
        </main>
      )}
      {page === "detail" && selected && (
        <main className="nova-detail section">
          <button className="back" onClick={() => go("shop")}>
            ← BACK TO COLLECTION
          </button>
          <div className="nova-detail-grid">
            <div className="nova-gallery">
              <img
                className="main-image"
                src={selected.images[activeImage]}
                alt={selected.title}
              />
              <div className="nova-thumbs">
                {selected.images.map((im, i) => (
                  <button
                    key={im}
                    className={i === activeImage ? "active" : ""}
                    onClick={() => setActiveImage(i)}
                  >
                    <img src={im} alt={`${selected.title} view ${i + 1}`} />
                    <span>0{i + 1}</span>
                  </button>
                ))}
              </div>
            </div>
            <aside>
              <span className="eyebrow">{selected.category} / NOVA SERIES</span>
              <h1>{selected.title}</h1>
              <div className="price">${selected.price}.00</div>
              <p>{selected.description}</p>
              <ul className="product-highlights">
                {selected.highlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
              <label>FINISH · GRAPHITE</label>
              <div className="swatches">
                <button className="black" />
                <button className="stone" />
                <button className="blue" />
              </div>
              <button
                className="primary add"
                onClick={() => {
                  add(selected);
                  go("cart");
                }}
              >
                ADD TO CART · ${selected.price}
              </button>
              <details open>
                <summary>CORE SPECIFICATION</summary>
                <dl className="spec-list">
                  {selected.specs.map(([name, value]) => (
                    <div key={name}>
                      <dt>{name}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </details>
              <details>
                <summary>IN THE BOX</summary>
                <p>
                  {selected.title}, {selected.inBox}
                </p>
              </details>
              <details>
                <summary>DELIVERY, RETURNS & CARE</summary>
                <p>
                  Free tracked standard delivery on orders over $120. Try it for
                  30 days, with straightforward returns and two-year Somerset
                  Care for manufacturing defects.
                </p>
              </details>
            </aside>
          </div>
        </main>
      )}
      {page === "cart" && (
        <main className="cart-page section">
          <div className="section-title">
            <h1>Your cart.</h1>
            <p>
              {count} item{count === 1 ? "" : "s"}
            </p>
          </div>
          {!cart.length ? (
            <div className="empty">
              <h2>Space for something new.</h2>
              <p>Your cart is empty.</p>
              <button className="primary" onClick={() => shop("ALL")}>
                SHOP THE COLLECTION
              </button>
            </div>
          ) : (
            <div className="cart-layout">
              <section>
                {cart.map((p) => (
                  <article className="cart-row" key={p.id}>
                    <img src={p.images[0]} alt={p.title} />
                    <div>
                      <span className="eyebrow">{p.category}</span>
                      <h2>{p.title}</h2>
                      <p>Graphite · Quantity {p.qty}</p>
                      <button
                        onClick={() =>
                          setCart((c) => c.filter((x) => x.id !== p.id))
                        }
                      >
                        REMOVE
                      </button>
                    </div>
                    <b>${p.price * p.qty}.00</b>
                  </article>
                ))}
              </section>
              <aside className="summary">
                <h2>Order summary</h2>
                <div>
                  <span>Subtotal</span>
                  <b>${total}.00</b>
                </div>
                <div>
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
                <hr />
                <div>
                  <b>Estimated total</b>
                  <b>${total}.00</b>
                </div>
                <button className="primary" onClick={() => go("checkout")}>
                  SECURE CHECKOUT →
                </button>
                <small>
                  Taxes and regional duties are shown before payment.
                </small>
              </aside>
            </div>
          )}
        </main>
      )}
      {page === "policy" && (
        <main className="policy section">
          <div className="policy-masthead">
            <span className="eyebrow">SOMERSET / CUSTOMER POLICIES</span>
            <h1>{somersetPolicies[policy].title}</h1>
            <div className="policy-meta">
              <span>LAST UPDATED</span>
              <b>OCTOBER 9, 2026</b>
              <span>READING TIME</span>
              <b>
                {Math.max(
                  5,
                  Math.ceil(policyWordCount(somersetPolicies[policy]) / 220),
                )}{" "}
                MIN ·{" "}
                {policyWordCount(somersetPolicies[policy]).toLocaleString()}{" "}
                WORDS
              </b>
            </div>
          </div>
          <div className="policy-layout">
            <aside className="policy-nav" aria-label="Information pages">
              <span className="eyebrow">IN THIS SECTION</span>
              {somersetPolicyNav.map(([key, label], index) => (
                <button
                  key={key}
                  className={policy === key ? "active" : ""}
                  onClick={() => showPolicy(key)}
                >
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {label}
                </button>
              ))}
            </aside>
            <article className="policy-content">
              <p className="policy-intro">{somersetPolicies[policy].intro}</p>
              <div className="policy-sections">
                {somersetPolicies[policy].sections.map(
                  ([heading, body], index) => (
                    <section key={heading}>
                      <span className="policy-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <h2>{heading}</h2>
                        <p>{body}</p>
                      </div>
                    </section>
                  ),
                )}
              </div>
              <div className="policy-help">
                <span className="eyebrow">STILL HAVE A QUESTION?</span>
                <h2>We’re here to help.</h2>
                <a className="text-btn" href="mailto:support@somerset.example">
                  CONTACT SOMERSET →
                </a>
              </div>
            </article>
          </div>
        </main>
      )}
      <footer>
        <div className="footer-grid">
          <div>
            <button className="brand lightbrand" onClick={() => go("home")}>
              <i />
              somerset
            </button>
            <p className="mt-4">Contact us</p>
            <p className="mt-4 leading-[1.5]">
              275 Prince Albert St, Ottawa, ON K1K 1Z4 CA
            </p>
          </div>
          <div>
            <b>SHOP</b>
            <button onClick={() => shop("KEYBOARDS")}>Keyboards</button>
            <button onClick={() => shop("MICE")}>Mice</button>
            <button onClick={() => shop("AUDIO")}>Audio</button>
          </div>
          <div className="footer-policies">
            <b>ALL POLICIES · {somersetPolicyNav.length}</b>
            <div className="footer-policy-links">
              {somersetPolicyNav.map(([key, label]) => (
                <button key={key} onClick={() => showPolicy(key)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <b>CONTACT</b>
            <a href="mailto:support@somerset.example">Contact</a>
            <a href="mailto:privacy@somerset.example">Privacy team</a>
            <a href="mailto:accessibility@somerset.example">Accessibility</a>
          </div>
        </div>
        <div className="copyright">© 2026 SOMERSET · ALL RIGHTS RESERVED</div>
      </footer>
    </div>
  );
}
function Card({ p, open, index }) {
  return (
    <article className="product-card" onClick={() => open(p)}>
      <div className="product-img">
        <img src={p.images[0]} alt={p.title} />
        {index < 2 && <span>NEW</span>}
        <button>VIEW PRODUCT →</button>
      </div>
      <div>
        <small>{p.category}</small>
        <b>{p.title}</b>
        <strong>${p.price}</strong>
      </div>
    </article>
  );
}
export default App;
